import { Component, OnInit, Input, OnChanges, SimpleChanges, AfterViewInit, OnDestroy } from "@angular/core";
import * as L from 'leaflet';
import { GeoAssetDetailDTO } from 'src/api/models';
import 'leaflet-draw';

@Component({
  selector: 'app-geo-asset-editor',
  templateUrl: './geo-asset-editor.component.html',
  styleUrls: ['./geo-asset-editor.component.css']
})
export class GeoAssetEditorComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() geoAsset: GeoAssetDetailDTO;

  private map: L.Map;
  private drawnItems: L.FeatureGroup;
  private currentLayer: L.Layer;

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['geoAsset']) {
      if (this.map) {
        // Clear existing layers first
        if (this.drawnItems) {
          this.drawnItems.clearLayers();
        }
        
        if (this.geoAsset) {
          this.loadGeoAssetGeometry();
          // Refresh map to fix rendering issues
          setTimeout(() => {
            this.map.invalidateSize();
          }, 100);
        }
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up map when component is destroyed
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initMap(): void {
    // Initialize map
    this.map = L.map('geoAssetMap', {
      center: [-31.9505, 115.8605], // Default to Perth, Australia
      zoom: 13
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(this.map);

    // Initialize feature group for drawn items
    this.drawnItems = new L.FeatureGroup();
    this.map.addLayer(this.drawnItems);

    // Add draw control
    const drawControl = new L.Control.Draw({
      edit: {
        featureGroup: this.drawnItems
      },
      draw: {
        polygon: true,
        polyline: false,
        rectangle: true,
        circle: false,
        marker: false,
        circlemarker: false
      }
    });
    this.map.addControl(drawControl);

    // Handle draw events
    this.map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer;
      this.drawnItems.addLayer(layer);
      this.currentLayer = layer;
      console.log('Geometry created:', this.getWKTFromLayer(layer));
    });

    // Load geometry if geoAsset is already available
    if (this.geoAsset) {
      this.loadGeoAssetGeometry();
    }

    // Refresh map after a short delay to ensure proper rendering
    setTimeout(() => {
      this.map.invalidateSize();
    }, 200);
  }

  private loadGeoAssetGeometry(): void {
    if (!this.geoAsset?.geometryWKT) {
      console.log('No geometry WKT available');
      return;
    }

    console.log('Loading geometry WKT:', this.geoAsset.geometryWKT);

    // Clear existing layers
    this.drawnItems.clearLayers();

    try {
      // Parse WKT and add to map
      const layer = this.parseWKT(this.geoAsset.geometryWKT);
      if (layer) {
        console.log('Layer created successfully, adding to map');
        this.drawnItems.addLayer(layer);
        this.currentLayer = layer;
        
        // Fit map to geometry bounds with animation
        const bounds = this.drawnItems.getBounds();
        if (bounds.isValid()) {
          console.log('Fitting bounds:', bounds);
          setTimeout(() => {
            this.map.fitBounds(bounds, { 
              padding: [500, 500],
              maxZoom: 16,
              animate: true,
              duration: 0.5
            });
          }, 150);
        }
      } else {
        console.warn('Failed to parse WKT - no layer created');
      }
    } catch (error) {
      console.error('Error parsing WKT:', error);
    }
  }

  private parseWKT(wkt: string): L.Layer | null {
    if (!wkt) return null;

    console.log('Parsing WKT:', wkt);

    // Parser for MULTIPOLYGON
    // Format: MULTIPOLYGON (((lng lat, lng lat, ...)), ((lng lat, lng lat, ...)))
    if (wkt.startsWith('MULTIPOLYGON')) {
      console.log('Parsing MULTIPOLYGON');
      
      // Extract all polygon rings - each is wrapped in ((...))
      // Use non-greedy match to capture content between (( and ))
      const polygonRegex = /\(\((.+?)\)\)/g;
      const polygons: [number, number][][] = [];
      let match;
      
      while ((match = polygonRegex.exec(wkt)) !== null) {
        let coordString = match[1];
        // Clean any stray parentheses from the start/end
        coordString = coordString.replace(/^\(+|\)+$/g, '');
        console.log('Found polygon coordinates:', coordString);
        const coords = coordString.split(',').map(pair => {
          const [lng, lat] = pair.trim().split(/\s+/).map(Number);
          return [lat, lng] as [number, number];
        });
        polygons.push(coords);
      }
      
      console.log('Parsed', polygons.length, 'polygon(s)');
      
      if (polygons.length === 1) {
        return L.polygon(polygons[0], {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2
        });
      } else if (polygons.length > 1) {
        // Create a layer group with multiple polygons
        const layerGroup = L.featureGroup();
        polygons.forEach(coords => {
          const polygon = L.polygon(coords, {
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.2
          });
          layerGroup.addLayer(polygon);
        });
        return layerGroup;
      }
    }

    // Simple WKT parser for POLYGON
    // Format: POLYGON((lng lat, lng lat, ...))
    const polygonMatch = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/);
    if (polygonMatch) {
      console.log('Matched POLYGON pattern');
      const coordString = polygonMatch[1];
      console.log('Coordinate string:', coordString);
      
      const coords = coordString.split(',').map(pair => {
        const [lng, lat] = pair.trim().split(/\s+/).map(Number);
        return [lat, lng] as [number, number];
      });
      
      console.log('Parsed coordinates:', coords);
      
      return L.polygon(coords, {
        color: '#3388ff',
        fillColor: '#3388ff',
        fillOpacity: 0.2
      });
    }

    console.warn('No WKT pattern matched');
    return null;
  }

  private getWKTFromLayer(layer: any): string {
    if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
      const latlngs = layer.getLatLngs()[0] as L.LatLng[];
      const coords = latlngs.map(ll => `${ll.lng} ${ll.lat}`);
      
      // Reverse coordinates for counter-clockwise orientation (SQL Server geography requirement)
      coords.reverse();
      
      // Close the polygon by adding the first coordinate at the end
      if (coords.length > 0) {
        coords.push(coords[0]);
      }
      
      return `POLYGON((${coords.join(', ')}))`;
    }
    return '';
  }

  public getGeometryWKT(): string {
    const layers = this.drawnItems.getLayers();
    
    if (layers.length === 0) {
      return '';
    }
    
    if (layers.length === 1) {
      // Single polygon - return as POLYGON
      return this.getWKTFromLayer(layers[0]);
    }
    
    // Multiple polygons - return as MULTIPOLYGON
    const polygons: string[] = [];
    
    layers.forEach((layer: any) => {
      if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        const coords = latlngs.map(ll => `${ll.lng} ${ll.lat}`);
        
        // Reverse coordinates for counter-clockwise orientation
        coords.reverse();
        
        // Close the polygon
        if (coords.length > 0) {
          coords.push(coords[0]);
        }
        
        polygons.push(`((${coords.join(', ')}))`);
      }
    });
    
    return `MULTIPOLYGON(${polygons.join(', ')})`;
  }

  public refreshMap(): void {
    if (this.map) {
      this.map.invalidateSize();
    }
  }
}